import { useState } from 'react';
import { getAlpacaConnectUrl, disconnectAlpaca } from '../api';

const POLICY_LINKS = [
  { label: 'Terms and Conditions', href: '/terms' },
  { label: 'Privacy Policy', href: '/privacy' },
  { label: 'Pricing Schedule', href: '/pricing' },
  { label: 'Cybersecurity Policy', href: '/cybersecurity' },
];

export default function ConnectPage({ alpaca, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [docsAgreed, setDocsAgreed] = useState(false);
  const [openedDocs, setOpenedDocs] = useState({});
  const connected = alpaca?.connected;
  const allDocsOpened = POLICY_LINKS.every(link => openedDocs[link.href]);

  const handleDisconnect = async () => {
    setBusy(true);
    try { await disconnectAlpaca(); } catch { /* ignore */ }
    setBusy(false);
    onRefresh();
  };

  const handleConnect = async () => {
    setBusy(true);
    try {
      const url = await getAlpacaConnectUrl();
      window.location.href = url;
    } catch (err) {
      console.error('Failed to get Alpaca connect URL:', err);
      setBusy(false);
    }
  };

  return (
    <div>
      <h1 style={s.h1}>Brokerage Connection</h1>
      <p style={s.desc}>Connect your Alpaca account to view account information and submit orders only after your approval.</p>
      <div style={s.card}>
        <div style={s.cardHead}>
          <div style={s.broker}>
            <div style={s.brokerIcon}>A</div>
            <div>
              <div style={s.brokerName}>Alpaca Markets</div>
              <div style={s.brokerSub}>Paper trading onboarding · OAuth 2.0</div>
            </div>
          </div>
          <span style={{...s.statusPill,
            background: connected ? 'var(--green-bg)' : 'var(--red-bg)',
            color: connected ? 'var(--green)' : 'var(--red)',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
            {connected ? 'Connected' : 'Not Connected'}
          </span>
        </div>
        {connected ? (
          <>
            <div style={s.statsGrid}>
              <Stat label="Account" value={alpaca.account_id} />
              <Stat label="Portfolio Value" value={fmt(alpaca.portfolio_value)} />
              <Stat label="Buying Power" value={fmt(alpaca.buying_power)} />
              <Stat label="Cash" value={fmt(alpaca.cash)} />
            </div>
            <div style={s.cardFoot}>
              <button onClick={handleDisconnect} disabled={busy} style={s.dangerBtn}>
                {busy ? 'Disconnecting...' : 'Disconnect Account'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={s.steps}>
              <Step n="1" text="Authorize via Alpaca's secure OAuth — we never see your password" />
              <Step n="2" text="Complete at least one week of paper trading before live-trading workflow access" />
              <Step n="3" text="Revoke access anytime from this page or Alpaca dashboard" />
            </div>
            <div style={s.disclosure}>
              <div style={s.policyBox}>
                <strong>Required Documents</strong>
                <p style={s.policyText}>
                  Before connecting your Alpaca account, please review Thankful Money's Terms and Conditions,
                  Privacy Policy, Pricing Schedule, and Cybersecurity Policy.
                </p>
                <div style={s.policyLinks}>
                  {POLICY_LINKS.map(link => (
                    <a
                      key={link.href}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setOpenedDocs(prev => ({ ...prev, [link.href]: true }))}
                      style={{
                        ...s.policyLink,
                        borderColor: openedDocs[link.href] ? 'rgba(16,185,129,0.45)' : 'var(--border)',
                        background: openedDocs[link.href] ? 'var(--green-bg)' : 'var(--bg-2)',
                      }}
                    >
                      {openedDocs[link.href] ? '✓ ' : ''}{link.label}
                    </a>
                  ))}
                </div>
                <div style={allDocsOpened ? s.policyReady : s.policyHint}>
                  {allDocsOpened
                    ? 'All required documents have been opened. Please acknowledge after reviewing them.'
                    : `Open and review all required documents to continue (${Object.keys(openedDocs).length}/${POLICY_LINKS.length} opened).`}
                </div>
                <label style={s.checkLabel}>
                  <input
                    type="checkbox"
                    checked={docsAgreed}
                    disabled={!allDocsOpened}
                    onChange={e => setDocsAgreed(e.target.checked)}
                    style={{ ...s.checkbox, opacity: allDocsOpened ? 1 : 0.45 }}
                  />
                  <span>I opened, reviewed, and agree to the Terms and Conditions, Privacy Policy, Pricing Schedule, and Cybersecurity Policy.</span>
                </label>
              </div>
              <div style={s.disclosureText}>
                <strong>Authorization Disclosure</strong><br />
                Authorize Thankful Money. By allowing Thankful Money to access your Alpaca account,
                you are granting Thankful Money access to your account information and authorization
                to place transactions in your account at your direction. Alpaca does not warrant or
                guarantee that Thankful Money will work as advertised or expected. Before authorizing,
                learn more about Thankful Money at thankfulmoney.com.
              </div>
              <label style={s.checkLabel}>
                <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} style={s.checkbox} />
                <span>I have read and agree to the authorization disclosure above</span>
              </label>
            </div>
            <div style={s.cardFoot}>
              <button
                onClick={handleConnect}
                disabled={!agreed || !docsAgreed || busy}
                style={{
                  ...s.primaryBtn,
                  opacity: agreed && docsAgreed ? 1 : 0.4,
                  cursor: agreed && docsAgreed ? 'pointer' : 'not-allowed',
                }}>
                {busy ? 'Connecting...' : 'Connect Alpaca Account'}
              </button>
              {(!agreed || !docsAgreed) && <span style={s.checkHint}>Please accept the required documents and authorization disclosure to continue</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (<div style={s.stat}><div style={s.statLabel}>{label}</div><div style={s.statVal}>{value}</div></div>);
}
function Step({ n, text }) {
  return (<div style={s.step}><div style={s.stepN}>{n}</div><div style={s.stepText}>{text}</div></div>);
}
function fmt(v) {
  return v ? `$${Number(v).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}` : '—';
}

const s = {
  h1: { fontSize: 22, fontWeight: 700, marginBottom: 6 },
  desc: { fontSize: 14, color: 'var(--t3)', marginBottom: 28 },
  card: { background: 'var(--bg-1)', borderRadius: 'var(--rl)', border: '1px solid var(--border)', overflow: 'hidden' },
  cardHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid var(--border-s)' },
  broker: { display: 'flex', alignItems: 'center', gap: 14 },
  brokerIcon: { width: 42, height: 42, borderRadius: 10, background: '#FACC15', color: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 18 },
  brokerName: { fontSize: 15, fontWeight: 600 },
  brokerSub: { fontSize: 12, color: 'var(--t3)', marginTop: 2 },
  statusPill: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  statsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, background: 'var(--border-s)' },
  stat: { padding: '18px 24px', background: 'var(--bg-1)' },
  statLabel: { fontSize: 11, fontWeight: 600, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 },
  statVal: { fontSize: 18, fontWeight: 600 },
  cardFoot: { padding: '16px 24px', borderTop: '1px solid var(--border-s)' },
  primaryBtn: { display: 'inline-flex', padding: '10px 28px', background: 'var(--green)', color: '#000', borderRadius: 8, fontWeight: 600, fontSize: 14, textDecoration: 'none' },
  dangerBtn: { padding: '8px 18px', background: 'transparent', color: 'var(--red)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, fontSize: 13, fontWeight: 500 },
  steps: { padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 },
  step: { display: 'flex', alignItems: 'flex-start', gap: 14 },
  stepN: { width: 26, height: 26, borderRadius: '50%', background: 'var(--bg-3)', color: 'var(--t2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 },
  stepText: { fontSize: 13, color: 'var(--t2)', lineHeight: 1.6, paddingTop: 3 },
  disclosure: { margin: '0 24px 16px', padding: '16px 20px', background: 'var(--bg-3)', borderRadius: 'var(--r)', border: '1px solid var(--border-s)' },
  policyBox: { marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border-s)' },
  policyText: { fontSize: 12, color: 'var(--t3)', lineHeight: 1.7, marginTop: 6, marginBottom: 12 },
  policyLinks: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 },
  policyLink: {
    display: 'inline-flex', padding: '5px 10px', borderRadius: 6,
    border: '1px solid var(--border)', background: 'var(--bg-2)',
    color: 'var(--green)', fontSize: 12, fontWeight: 600, textDecoration: 'none',
  },
  policyHint: { fontSize: 12, color: 'var(--amber)', marginBottom: 12 },
  policyReady: { fontSize: 12, color: 'var(--green)', marginBottom: 12 },
  disclosureText: { fontSize: 12, color: 'var(--t3)', lineHeight: 1.7, marginBottom: 14 },
  checkLabel: { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: 'var(--t2)', cursor: 'pointer', lineHeight: 1.5 },
  checkbox: { marginTop: 3, accentColor: 'var(--green)', width: 16, height: 16, flexShrink: 0 },
  checkHint: { marginLeft: 16, fontSize: 12, color: 'var(--amber)' },
};
